import { isSameDay } from 'date-fns';
import { useUpdateTask } from 'features/tasks';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { Task } from 'shared/types/task';
import { getScheduleByDate } from 'shared/utils/schedules';

import { UpcomingPeriod, UpcomingPeriodTypes } from '../upcoming/types';

export type UsePeriodTargetOptions = {
  task: Task;
  period: UpcomingPeriod;
};

export const usePeriodTarget = ({ task, period }: UsePeriodTargetOptions) => {
  const today = useToday();
  const { submit: updateTask } = useUpdateTask();

  const plannedCount = useMemo(
    () =>
      period.days.filter(
        (day) =>
          day.entry &&
          !day.entry.isGeneratedFrequencyEntry &&
          !day.entry.hidden,
      ).length,
    [period.days],
  );

  const targetCount = useMemo(() => {
    if (!task.schedules || period.type === UpcomingPeriodTypes.Overdue) {
      return 0;
    }

    const activeSchedule = getScheduleByDate(task.schedules, {
      date: period.date,
      today,
    });
    return activeSchedule?.frequency?.count ?? 0;
  }, [task.schedules, period.date, period.type, today]);

  const showTargetCounter =
    (!!plannedCount || period.days.some((day) => day.entry?.hidden)) &&
    !!targetCount;

  const showRestoreAction = useMemo(() => {
    if (!targetCount) {
      return false;
    }

    const { activeCount, hiddenSkippedCount } = period.days.reduce(
      (acc, day) => {
        if (day.entry && !day.entry.hidden) {
          acc.activeCount += 1;
        }
        if (day.entry?.hidden && day.entry.state === ScheduleEntryState.Skip) {
          acc.hiddenSkippedCount += 1;
        }
        return acc;
      },
      { activeCount: 0, hiddenSkippedCount: 0 },
    );

    return !activeCount && !!hiddenSkippedCount;
  }, [period.days, targetCount]);

  const handleRestore = () => {
    if (!task.schedules?.length) {
      return;
    }

    const datesToRestore = period.days
      .filter(
        (day) =>
          day.entry?.hidden && day.entry.state === ScheduleEntryState.Skip,
      )
      .map((d) => d.date);

    if (!datesToRestore.length) {
      return;
    }

    const updatedSchedules = task.schedules.map((schedule) => {
      const newEntries = schedule.entries.filter(
        (entry) => !datesToRestore.some((date) => isSameDay(date, entry.date)),
      );

      const newSkips =
        schedule.skips?.filter(
          (skip) => !datesToRestore.some((date) => isSameDay(date, skip)),
        ) ?? [];

      return {
        ...schedule,
        entries: newEntries,
        skips: newSkips,
      };
    });

    updateTask({ id: task.id, schedules: updatedSchedules });
  };

  return {
    plannedCount,
    targetCount,
    showTargetCounter,
    showRestoreAction,
    handleRestore,
  };
};
